import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";

import {
  AssignTechnicianPayload,
  HubOption,
  MaintenanceAnalytics,
  MaintenanceFilters,
  MaintenanceListResponse,
  Pagination,
  ServiceHistory,
  ServiceTicket,
  ServiceTicketApiRow,
  ServiceTicketDetails,
  ServiceTicketPayload,
  SpareIssueOption,
  SpareIssuePayload,
  SpareReturnPayload,
  Technician,
  TicketStatus,
  TicketTimeline,
  VehicleOption,
  CloseTicketPayload,
} from "../types";

const TICKETS_URL = "/maintenance/service-tickets";
const ASSIGNMENTS_URL = "/maintenance/service-assignments";
const SPARE_ISSUES_URL = "/maintenance/spare-issues";

export const mapTicket = (ticket: ServiceTicketApiRow): ServiceTicket => ({
  id: ticket.id,
  ticketNumber: ticket.code,
  vehicleId: ticket.vehicleId,
  vehicleNumber: ticket.vehicleId,
  chassisNumber: ticket.chassisNumber,
  warehouseId: ticket.warehouseId,
  riderId: ticket.riderId ?? null,
  clientName: ticket.clientName ?? null,
  clientRiderID: ticket.clientRiderID ?? null,
  serviceType: ticket.serviceType,
  priority: ticket.priority,
  status: ticket.status,
  issueTitle: ticket.title,
  issueDescription: ticket.description,
  imageUrl: ticket.imageUrl,
  assignedTechnicianId: ticket.assignedToId,
  assignedTechnicianName: ticket.assignedToId || "-",
  remarks: ticket.remarks,
  actualRepairCost: ticket.actualRepairCost,
  resolution: ticket.resolution,
  closureRemarks: ticket.closureRemarks,
  createdAt: ticket.createdAt,
  updatedAt: ticket.updatedAt,
  closedAt: ticket.closedAt,
  expiresAt: ticket.expiresAt,
  expiredAt: ticket.expiredAt,
  serviceInvoice: ticket.serviceInvoice,
});

const toServiceTicketRequest = (payload: ServiceTicketPayload) => ({
  vehicleId: payload.vehicleId,
  warehouseId: payload.hubId,
  serviceType: payload.serviceType,
  priority: payload.priority,
  title: payload.issueTitle,
  description: payload.issueDescription,
  city: payload.city,
  reportedById: payload.reportedById,
  technicianId: payload.technicianId,
  remarks: payload.remarks,
});

const employeeName = (employee: any) => {
  const userName = [employee.user?.firstName, employee.user?.lastName]
    .filter(Boolean)
    .join(" ");
  const directName = [employee.firstName, employee.lastName].filter(Boolean).join(" ");

  return (
    employee.name ||
    employee.fullName ||
    employee.employeeName ||
    userName ||
    directName ||
    employee.employeeId ||
    employee.code ||
    employee.id
  );
};

class MaintenanceService {
  async getServiceTickets(
    filters?: MaintenanceFilters
  ): Promise<MaintenanceListResponse> {
    const response = await sparePartsApi.get<ApiResponse<ServiceTicketApiRow[]>>(
      TICKETS_URL,
      { params: filters }
    );

    return {
      items: (response.data.data || []).map(mapTicket),
      pagination: response.data.meta as Pagination,
    };
  }

  async getServiceTicketById(id: string): Promise<ServiceTicket> {
    const response = await sparePartsApi.get<ApiResponse<ServiceTicketApiRow>>(
      `${TICKETS_URL}/${id}`
    );

    return mapTicket(response.data.data);
  }

  async getServiceTicketDetails(id: string): Promise<ServiceTicketDetails> {
    const response = await sparePartsApi.get<ApiResponse<ServiceTicketDetails>>(
      `${TICKETS_URL}/${id}`
    );

    return response.data.data;
  }

  async createServiceTicket(payload: ServiceTicketPayload): Promise<ServiceTicket> {
    const response = await sparePartsApi.post<ApiResponse<ServiceTicketApiRow>>(
      TICKETS_URL,
      toServiceTicketRequest(payload)
    );

    return mapTicket(response.data.data);
  }

  async updateServiceTicket(
    id: string,
    payload: ServiceTicketPayload
  ): Promise<ServiceTicket> {
    const response = await sparePartsApi.put<ApiResponse<ServiceTicketApiRow>>(
      `${TICKETS_URL}/${id}`,
      toServiceTicketRequest(payload)
    );

    return mapTicket(response.data.data);
  }

  async assignTechnician(
    ticketId: string,
    payload: AssignTechnicianPayload
  ): Promise<void> {
    await sparePartsApi.post(ASSIGNMENTS_URL, {
      ticketId,
      technicianId: payload.technicianId,
      assignedById: payload.assignedById || "ADMIN",
      remarks: payload.remarks,
    });
  }

  async updateTicketStatus(
    id: string,
    status: TicketStatus,
    payload?: Pick<CloseTicketPayload, "actualCost">
  ): Promise<ServiceTicket> {
    const response = await sparePartsApi.patch<ApiResponse<ServiceTicketApiRow>>(
      `${TICKETS_URL}/${id}/status`,
      {
        status,
        ...(payload?.actualCost !== undefined
          ? { actualCost: payload.actualCost }
          : {}),
      }
    );

    return mapTicket(response.data.data);
  }

  async closeTicket(
    id: string,
    payload?: CloseTicketPayload
  ): Promise<ServiceTicket> {
    const response = await sparePartsApi.patch<ApiResponse<ServiceTicketApiRow>>(
      `${TICKETS_URL}/${id}/status`,
      {
        status: "CLOSED",
        resolution: payload?.resolution,
        remarks: payload?.remarks,
      }
    );

    return mapTicket(response.data.data);
  }

  async deleteServiceTicket(id: string): Promise<void> {
    await sparePartsApi.delete(`${TICKETS_URL}/${id}`);
  }

  async getTicketTimeline(id: string): Promise<TicketTimeline[]> {
    const response = await sparePartsApi.get<ApiResponse<any>>(
      `${TICKETS_URL}/${id}`
    );
    const ticket = response.data.data || {};
    const events: TicketTimeline[] = [];

    if (ticket.createdAt || ticket.openedAt) {
      events.push({
        id: `${ticket.id}-raised`,
        ticketId: ticket.id,
        status: "RAISED",
        title: "Ticket Raised",
        description: ticket.title || "Service ticket created.",
        remarks: ticket.remarks,
        createdBy: ticket.createdById || "System",
        createdAt: ticket.createdAt || ticket.openedAt,
      });
    }

    (ticket.assignments || []).forEach((assignment: any) => {
      events.push({
        id: assignment.id,
        ticketId: ticket.id,
        status: "ASSIGNED",
        title: "Technician Assigned",
        description: `Assigned to ${assignment.technicianId || "-"}.`,
        remarks: assignment.remarks,
        createdBy: assignment.assignedById || "AME",
        createdAt: assignment.assignedAt || assignment.createdAt,
      });

      if (assignment.acceptedAt) {
        events.push({
          id: `${assignment.id}-accepted`,
          ticketId: ticket.id,
          status: "IN_PROGRESS",
          title: "Work Accepted",
          description: "Technician accepted the assignment.",
          createdBy: assignment.technicianId || "Technician",
          createdAt: assignment.acceptedAt,
        });
      }

      if (assignment.completedAt) {
        events.push({
          id: `${assignment.id}-completed`,
          ticketId: ticket.id,
          status: "COMPLETED",
          title: "Assignment Completed",
          description: "Technician completed assigned work.",
          createdBy: assignment.technicianId || "Technician",
          createdAt: assignment.completedAt,
        });
      }
    });

    (ticket.spareIssues || []).forEach((issue: any) => {
      const items = (issue.items || [])
        .map((item: any) => {
          const name = item.sparePart?.name || item.sparePartId;
          return `${name} x ${item.quantity}`;
        })
        .join(", ");

      events.push({
        id: issue.id,
        ticketId: ticket.id,
        status: "ISSUED",
        title: `Spare Issue ${issue.code || ""}`.trim(),
        description: items || "Spare parts issued.",
        remarks: issue.remarks,
        createdBy: issue.issuedById || "AME",
        createdAt: issue.issuedAt || issue.createdAt,
      });
    });

    (ticket.spareReturns || []).forEach((spareReturn: any) => {
      events.push({
        id: spareReturn.id,
        ticketId: ticket.id,
        status:
          spareReturn.status === "VERIFIED" ? "RETURN_VERIFIED" : "RETURN_PENDING",
        title:
          spareReturn.status === "VERIFIED"
            ? "Spare Return Verified"
            : "Spare Return Submitted",
        description: `${spareReturn.returnCode || "Return"} - ${(
          spareReturn.items || []
        ).reduce(
          (total: number, item: any) => total + Number(item.returnedQty || 0),
          0
        )} units returned.`,
        remarks: spareReturn.remarks,
        createdBy: spareReturn.verifiedBy || spareReturn.technicianId || "Technician",
        createdAt: spareReturn.verifiedAt || spareReturn.returnedAt || spareReturn.createdAt,
      });
    });

    if (ticket.completedAt) {
      events.push({
        id: `${ticket.id}-completed`,
        ticketId: ticket.id,
        status: "COMPLETED",
        title: "Ticket Completed",
        description: "Service work completed.",
        createdBy: ticket.updatedById || "System",
        createdAt: ticket.completedAt,
      });
    }

    if (ticket.closedAt) {
      events.push({
        id: `${ticket.id}-closed`,
        ticketId: ticket.id,
        status: ticket.status === "CANCELLED" ? "CANCELLED" : "CLOSED",
        title: ticket.status === "CANCELLED" ? "Ticket Cancelled" : "Ticket Closed",
        description:
          ticket.status === "CANCELLED"
            ? "Ticket was cancelled."
            : ticket.resolution || "Ticket was closed.",
        remarks: ticket.closureRemarks || ticket.remarks,
        createdBy: ticket.updatedById || "System",
        createdAt: ticket.closedAt,
      });
    }

    const statusOrder: Record<string, number> = {
      RAISED: 10,
      ASSIGNED: 20,
      IN_PROGRESS: 30,
      ISSUED: 40,
      SPARE_ISSUED: 40,
      WAITING_FOR_PARTS: 45,
      COMPLETED: 50,
      RETURN_PENDING: 60,
      RETURN_VERIFIED: 70,
      CLOSED: 80,
      CANCELLED: 90,
    };

    return events
      .filter((event) => Boolean(event.createdAt))
      .sort(
        (a, b) =>
          (statusOrder[a.status] || 999) - (statusOrder[b.status] || 999) ||
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
  }

  async getServiceHistory(_vehicleId: string): Promise<ServiceHistory[]> {
    return [];
  }

  async getMaintenanceAnalytics(): Promise<MaintenanceAnalytics> {
    const response = await sparePartsApi.get<ApiResponse<any>>(
      "/analytics/maintenance"
    );

    const data = response.data.data || {};
    return {
      totalTickets: data.tickets?.total || 0,
      openTickets: data.tickets?.open || 0,
      assignedTickets: data.assignments?.total || 0,
      inProgressTickets: data.tickets?.inProgress || 0,
      waitingForParts: data.tickets?.waitingForParts || 0,
      completedTickets: data.tickets?.completed || 0,
      closedTickets: data.tickets?.closed || 0,
      cancelledTickets: data.tickets?.cancelled || 0,
      criticalTickets: data.tickets?.critical || 0,
      averageResolutionTime: data.tickets?.averageResolutionTime || 0,
      totalMaintenanceCost:
        data.invoices?.totalMaintenanceCost || data.invoices?.invoiceValue || 0,
    };
  }

  async getTechnicians(params?: { cityId?: string }): Promise<Technician[]> {
    const response = await sparePartsApi.get<ApiResponse<any[]>>(
      "/integrations/technicians",
      { params }
    );

    return (response.data.data || []).map((employee: any) => ({
      id: employee.employeeId || employee.employeeRecordId || employee.id,
      employeeId: employee.employeeCode || employee.employeeId,
      name: employee.employeeCode
        ? `${employee.employeeName || employeeName(employee)} (${employee.employeeCode})`
        : employee.employeeName || employeeName(employee),
      email: employee.email || employee.user?.email,
      mobile: employee.phone || employee.user?.phone,
    }));
  }

  async getAmes(params?: { cityId?: string }): Promise<Technician[]> {
    const response = await sparePartsApi.get<ApiResponse<any[]>>(
      "/integrations/ames",
      { params }
    );

    return (response.data.data || []).map((employee: any) => ({
      id: employee.employeeId || employee.employeeRecordId || employee.id,
      employeeId: employee.employeeCode || employee.employeeId,
      name: employee.employeeCode
        ? `${employee.employeeName || employeeName(employee)} (${employee.employeeCode})`
        : employee.employeeName || employeeName(employee),
      email: employee.email || employee.user?.email,
      mobile: employee.phone || employee.user?.phone,
    }));
  }

  async getHubs(): Promise<HubOption[]> {
    const response = await sparePartsApi.get<ApiResponse<HubOption[]>>(
      "/integrations/hubs"
    );

    return (response.data.data || []).map((hub) => ({
      id: hub.id,
      name: hub.code ? `${hub.name} (${hub.code})` : hub.name,
      code: hub.code,
      cityId: hub.cityId,
    }));
  }

  async getVehicles(params?: {
    hubId?: string;
    search?: string;
  }): Promise<VehicleOption[]> {
    const response = await sparePartsApi.get<ApiResponse<any[]>>(
      "/integrations/vehicles",
      {
        params: {
          page: 1,
          limit: 500,
          hubId: params?.hubId || undefined,
          search: params?.search || undefined,
        },
      }
    );

    return (response.data.data || []).map((vehicle: any) => {
      const modelName =
        vehicle.model?.modelName ||
        vehicle.model?.name ||
        vehicle.modelName ||
        vehicle.model ||
        "";
      const registrationNumber =
        vehicle.registrationNumber || vehicle.vehicleNumber || vehicle.code || vehicle.id;

      return {
        id: vehicle.id,
        registrationNumber,
        chassisNumber: vehicle.chassisNumber,
        modelName,
        hubId: vehicle.hubId || vehicle.hub?.id,
        hubName: vehicle.hub?.name || vehicle.hubName,
        label: [registrationNumber, modelName, vehicle.chassisNumber]
          .filter(Boolean)
          .join(" - "),
      };
    });
  }

  async getAvailableSpares(warehouseId: string): Promise<SpareIssueOption[]> {
    const [parts, stocks] = await Promise.all([
      sparePartsApi.get<ApiResponse<any[]>>("/spare-parts/dropdown"),
      sparePartsApi.get<ApiResponse<any[]>>("/inventory/stocks", {
        params: {
          page: 1,
          limit: 1000,
          warehouseId,
        },
      }),
    ]);

    const stockByPartId = new Map(
      (stocks.data.data || []).map((row: any) => [
        row.sparePartId,
        {
          availableStock: Number(row.availableQuantity || 0),
          unitCost: Number(row.weightedAverageUnitCost || row.unitCost || 0) || 1,
          partNumber: row.sparePart?.code || row.sparePart?.partNumber || row.code,
        },
      ])
    );

    return (parts.data.data || []).map((part: any) => {
      const stock = stockByPartId.get(part.id);

      return {
        id: part.id,
        name: part.name || part.sparePartName || part.id,
        partNumber: stock?.partNumber || part.code || part.partNumber || "-",
        availableStock: stock?.availableStock || 0,
        unitCost: stock?.unitCost || 1,
        maxQuantityPerServiceIssue: part.maxQuantityPerServiceIssue,
      };
    });
  }

  async issueSpares(payload: SpareIssuePayload): Promise<void> {
    await sparePartsApi.post(SPARE_ISSUES_URL, payload);
  }

  async returnSpares(payload: SpareReturnPayload): Promise<void> {
    await sparePartsApi.post("/maintenance/spare-returns", payload);
  }
}

export default new MaintenanceService();
